/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { handleResponse, withJsonFormat } from '@opengeoweb/api';
import { authFetch } from '@opengeoweb/authentication';
import { getCollections, getServiceInformation } from './api';
import type { EDRServiceInformation } from './api';
import type { EDRCollection } from '../../components/TimeSeries/types';

export interface EdrCollectionTarget {
  collection: EDRCollection;
  serviceUrl: string;
  serviceInformation: EDRServiceInformation;
}

export type EdrCollectionTargetErrorCode =
  | 'invalid-link'
  | 'not-edr'
  | 'service-unidentified'
  | 'not-advertised';

/** Failure with a stable code, so that the application can show a translated message. */
export class EdrCollectionTargetError extends Error {
  readonly code: EdrCollectionTargetErrorCode;

  constructor(code: EdrCollectionTargetErrorCode, message: string) {
    super(message);
    this.name = 'EdrCollectionTargetError';
    this.code = code;
  }
}

export const normalizeEdrServiceUrl = (href: string): string => {
  const url = new URL(href);
  url.hash = '';
  url.searchParams.delete('f');
  url.pathname = url.pathname.replace(/\/$/, '');
  return url.toString();
};

const requireHttpUrl = (href: string): URL => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    throw new EdrCollectionTargetError(
      'invalid-link',
      'The dataset link is not a valid URL.',
    );
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new EdrCollectionTargetError(
      'invalid-link',
      'The dataset link must use HTTP or HTTPS.',
    );
  }
  return url;
};

const getCollectionServiceUrl = (
  collection: EDRCollection,
  responseUrl: string,
): string => {
  const rootLink = collection.links?.find((link) => link.rel === 'root');
  if (rootLink) {
    return normalizeEdrServiceUrl(
      requireHttpUrl(new URL(rootLink.href, responseUrl).toString()).toString(),
    );
  }
  const selfLink = collection.links?.find(
    (link) =>
      link.rel === 'self' && link.type?.split(';')[0] === 'application/json',
  );
  const url = requireHttpUrl(
    new URL(selfLink?.href ?? responseUrl, responseUrl).toString(),
  );
  const marker = '/collections/';
  const markerIndex = url.pathname.lastIndexOf(marker);
  const collectionPath = url.pathname
    .slice(markerIndex + marker.length)
    .replace(/\/$/, '');
  if (
    markerIndex < 0 ||
    (collectionPath !== collection.id &&
      decodeURIComponent(collectionPath) !== collection.id)
  ) {
    throw new EdrCollectionTargetError(
      'service-unidentified',
      'The EDR service URL could not be identified from the collection link.',
    );
  }
  url.pathname = url.pathname.slice(0, markerIndex);
  url.search = '';
  url.hash = '';
  return normalizeEdrServiceUrl(url.toString());
};

/** Resolve a Records data link without assuming that the Record ID is an EDR ID. */
export const resolveEdrCollectionTarget = async (
  collectionUrl: string,
): Promise<EdrCollectionTarget> => {
  const requestUrl = withJsonFormat(requireHttpUrl(collectionUrl).toString());
  const response = await authFetch(requestUrl);
  const collection = (await handleResponse(response)) as EDRCollection;
  if (
    !collection ||
    typeof collection.id !== 'string' ||
    !collection.id ||
    (!collection.data_queries?.locations &&
      !collection.data_queries?.position) ||
    !collection.parameter_names ||
    typeof collection.parameter_names !== 'object'
  ) {
    throw new EdrCollectionTargetError(
      'not-edr',
      'This data link does not describe an EDR collection supported by Timeseries.',
    );
  }
  const serviceUrl = getCollectionServiceUrl(
    collection,
    response.url || requestUrl,
  );
  const [serviceInformation, listing] = await Promise.all([
    getServiceInformation(serviceUrl),
    getCollections(serviceUrl),
  ]);
  if (!listing.collections?.some((entry) => entry.id === collection.id)) {
    throw new EdrCollectionTargetError(
      'not-advertised',
      'The selected collection is not advertised by its EDR service.',
    );
  }
  return { collection, serviceUrl, serviceInformation };
};
